import config from './config.js'
import InterceptorManager from './interceptor.js'
import router from '../router'
import { ref } from 'vue'

/**
 * HTTP请求封装类 (Vue.js版本)
 * 提供统一的请求处理、拦截器管理和错误处理
 */
class HttpRequest {
  constructor () {
    this.baseURL = config.baseURL // 基础URL
    this.timeout = 15000 // 请求超时时间(毫秒)
    this.maxRetries = 2 // 最大重试次数

    // 拦截器实例
    this.requestInterceptor = new InterceptorManager()
    this.responseInterceptor = new InterceptorManager()

    // 请求队列(用于取消请求等操作)
    this.requestQueue = new Map()

    // 加载状态ref
    this.loading = ref(false)
    this.loadingText = ref('加载中...')
  }

  /**
   * 添加请求拦截器
   * @param {Function} onFulfilled - 成功处理函数
   * @param {Function} [onRejected] - 错误处理函数
   * @returns {Function} 移除该拦截器的函数
   */
  addRequestInterceptor (onFulfilled, onRejected) {
    return this.requestInterceptor.use(onFulfilled, onRejected)
  }

  /**
   * 添加响应拦截器
   * @param {Function} onFulfilled - 成功处理函数
   * @param {Function} [onRejected] - 错误处理函数
   * @returns {Function} 移除该拦截器的函数
   */
  addResponseInterceptor (onFulfilled, onRejected) {
    return this.responseInterceptor.use(onFulfilled, onRejected)
  }

  /**
   * 生成请求唯一标识
   * @param {Object} config - 请求配置
   * @returns {string} 请求key
   */
  generateRequestKey (config) {
    return `${config.method}_${config.url}_${JSON.stringify(config.data)}_${JSON.stringify(config.headers)}`
  }

  /**
   * 核心请求方法
   * @param {Object} options - 请求选项
   * @param {number} [retryCount=0] - 当前重试次数
   * @returns {Promise} 请求结果
   */
  async request (options, retryCount = 0) {
    const requestKey = this.generateRequestKey(options)

    try {
      // 合并配置
      let mergeOptions = {
        ...options,
        // url: this.baseURL + options.url,
        url: options.url,
        data: options.data || {},
        params: options.params || {},
        method: (options.method || 'GET').toUpperCase(),
        headers: this.setHeader(options.headers),
        timeout: this.timeout
      }

      // 执行请求拦截器
      mergeOptions = await this.requestInterceptor.run(mergeOptions)

      // 显示加载状态
      if (options.showLoading) {
        this.setLoading(true, options.loadingText || '加载中...')
      }

      // 创建请求控制器用于取消请求
      const controller = new AbortController()
      const signal = controller.signal
      this.requestQueue.set(requestKey, controller)

      // 发送请求
      const response = await fetch(mergeOptions.url, {
        method: mergeOptions.method,
        headers: {
          'Content-Type': 'application/json',
          ...mergeOptions.headers
        },
        body: mergeOptions.method !== 'GET' ? JSON.stringify(mergeOptions.data) : null,
        signal
        // credentials: 'include' // 如果需要发送cookies
      })

      // 从队列移除
      this.requestQueue.delete(requestKey)

      // 处理响应
      let responseData
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json()
      } else {
        responseData = await response.text()
      }

      const result = {
        data: responseData,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        config: mergeOptions
      }

      // 执行响应拦截器
      const processedResponse = await this.responseInterceptor.run(result)

      // Token过期处理(401)
      if (processedResponse.status === 401 && retryCount === 0) {
        try {
          // 这里可以添加token刷新逻辑
          // await refreshToken()
          return this.request(options, retryCount + 1)
        } catch (refreshError) {
          router.push('/')
          throw this.handleError({
            code: 401,
            message: '登录已过期，请重新登录'
          })
        }
      }

      // 业务逻辑错误处理
      if (processedResponse.data.code !== undefined && processedResponse.data.code !== 200) {
        throw this.handleBusinessError(processedResponse)
      }

      return processedResponse.data
    } catch (error) {
      // 请求重试
      if (retryCount < this.maxRetries && this.shouldRetry(error)) {
        return this.request(options, retryCount + 1)
      }

      // 统一错误提示
      if (options.showError !== false) {
        this.showMessage(error.message || '请求失败，请稍后重试', 'error')
      }
      return Promise.reject(error)
    } finally {
      // 从队列移除
      this.requestQueue.delete(requestKey)

      // 隐藏加载状态
      if (options.showLoading) {
        this.setLoading(false)
      }
    }
  }

  /**
   * 设置加载状态
   * @param {boolean} isLoading - 是否加载中
   * @param {string} [text='加载中...'] - 加载文本
   */
  setLoading (isLoading, text = '加载中...') {
    this.loading.value = isLoading
    this.loadingText.value = text
  }

  /**
   * 获取当前加载状态
   * @returns {Object} { loading: ref<boolean>, loadingText: ref<string> }
   */
  getLoadingState () {
    return {
      loading: this.loading,
      loadingText: this.loadingText
    }
  }

  /**
   * 判断是否需要重试
   * @param {Error} error - 错误对象
   * @returns {boolean} 是否需要重试
   */
  shouldRetry (error) {
    const retryableErrors = ['AbortError', 'TypeError', 'NetworkError']
    return retryableErrors.includes(error.name) ||
           error.message?.includes('network') ||
           error.message?.includes('failed')
  }

  /**
   * 设置请求头
   * @param {Object} [customHeader={}] - 自定义请求头
   * @returns {Object} 完整请求头
   */
  setHeader (customHeader = {}) {
    // const token = localStorage.getItem('token')
	var token = plus.storage.getItem('h5token')
    return {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      Authorization: token ? `Bearer ${token}` : '',
      ...customHeader
    }
  }

  /**
   * 处理网络错误
   * @param {Object} error - 错误对象
   * @returns {Object} 标准化错误对象
   */
  handleError (error) {
    const errorMap = {
      400: '请求参数错误',
      401: '未授权，请重新登录',
      403: '拒绝访问',
      404: '请求资源不存在',
      408: '请求超时',
      500: '服务器错误',
      501: '服务未实现',
      502: '网络错误',
      503: '服务不可用',
      504: '网络超时',
      505: 'HTTP版本不受支持'
    }

    return {
      code: error.code || 'NETWORK_ERROR',
      message: error.message || errorMap[error.status] || '网络连接失败，请稍后重试',
      originalError: error
    }
  }

  /**
   * 处理业务错误
   * @param {Object} response - 响应对象
   * @returns {Object} 标准化错误对象
   */
  handleBusinessError (response) {
    return {
      code: response.data?.code || 'BUSINESS_ERROR',
      message: response.data?.msg || response.data?.message || '业务处理失败',
      data: response.data,
      status: response.status
    }
  }

  /**
   * 显示提示消息
   * @param {string} msg - 消息内容
   * @param {string} [type='error'] - 消息类型 (error, success, warning, info)
   */
  showMessage (msg, type = 'error') {
    // 现在这个方法只是简单地在控制台打印
    // 在实际应用中，可以通过事件总线或provide/inject传递通知方法
    console[type](msg)

    // 或者抛出一个事件让组件监听
    // const event = new CustomEvent('http-message', { detail: { message: msg, type } })
    // window.dispatchEvent(event)
  }

  /**
   * 取消请求
   * @param {string} requestKey - 请求key
   */
  cancelRequest (requestKey) {
    const controller = this.requestQueue.get(requestKey)
    if (controller) {
      controller.abort()
      this.requestQueue.delete(requestKey)
    }
  }

  /**
   * 取消所有请求
   */
  cancelAllRequests () {
    this.requestQueue.forEach(controller => controller.abort())
    this.requestQueue.clear()
  }

  // 改进后的 GET 方法封装
  get (url, params = {}, options = {}) {
  // 安全处理 params 参数
    const safeParams = params || {}

    // 将params转换为查询字符串
    const queryString = Object.keys(safeParams)
      .filter(key => safeParams[key] !== undefined && safeParams[key] !== null) // 过滤掉 undefined 和 null 值
      .map(key =>
      `${encodeURIComponent(key)}=${encodeURIComponent(safeParams[key])}`
      )
      .join('&')

    const fullUrl = queryString ? `${url}?${queryString}` : url

    return this.request({
      url: fullUrl,
      method: 'GET',
      ...options
    })
  }

  post (url, data = {}, options = {}) {
    return this.request({
      url,
      data,
      method: 'POST',
      ...options
    })
  }

  put (url, data = {}, options = {}) {
    return this.request({
      url,
      data,
      method: 'PUT',
      ...options
    })
  }

  delete (url, data = {}, options = {}) {
    return this.request({
      url,
      data,
      method: 'DELETE',
      ...options
    })
  }
}
// 导出单例
export default new HttpRequest()
